// Chemin: C:\gestion-copro\src\components\auth\ProtectedRoute.tsx
// Protection des routes - Copro Protect
// Session 8 : ajout verification status (pending/active/rejected)
// Session 54 : ajout requireSaas pour Admin SaaS
// Session 56 : redirection automatique copropriétaires vers /espace-copro
// Session 57 : correction reconnaissance rôle CS
// Session 88 : vérification signature mandat de coordination pour membres CS
// Session 100 : Chantier ESCALATOR — ajout requireVigie + redirection vigie
// Session 139 : Chantier C8 Stripe — blocage VIGIE en_attente_paiement + relance Stripe
// Session 140 : Suppression casts as string/as any — UserStatus et statutAbonnement typés proprement
// Session 188 : Ajout requireApporteur + double rôle vigie+apporteur via apporteurInfo

import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import { getUserById } from '../../services/inscription.service';
import { checkSignatureRequise } from '../../services/mandat.service';
import { isEnAttentePaiement as checkEnAttentePaiement } from '../../types/user.types';
import { Shield, Eye, CreditCard, Loader2, Handshake } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireCS?: boolean;
  requireActive?: boolean;
  requireSaas?: boolean;
  /** Session 100 : si true, seuls les VIGIE et admin_saas peuvent accéder */
  requireVigie?: boolean;
  /** Session 188 : si true, seuls les apporteurs et admin_saas peuvent accéder */
  requireApporteur?: boolean;
  /** Session 88 : si true, ne vérifie PAS le mandat (évite boucle sur MandatSignaturePage) */
  skipMandatCheck?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireAdmin = false,
  requireCS = false,
  requireActive = false,
  requireSaas = false,
  requireVigie = false,
  requireApporteur = false,
  skipMandatCheck = false
}) => {
  const { firebaseUser, userProfile, loading: authLoading, isAuthenticated, isAdmin, isCS } = useAuth();
  const { coproprieteId } = useCopropriete();
  const location = useLocation();
  const [checking, setChecking] = useState(requireActive);
  const [userStatus, setUserStatus] = useState<string | null>(null);
  
  // Session 88 : état pour la vérification du mandat
  const [mandatCheckDone, setMandatCheckDone] = useState(false);
  const [mandatRedirect, setMandatRedirect] = useState<string | null>(null);

  // Session 139 : état pour relance paiement Stripe
  const [isRelancingPayment, setIsRelancingPayment] = useState(false);

  // Vérifier si l'utilisateur est Gestionnaire SaaS
  const isGestionnaireSaas = userProfile?.isGestionnaireSaas === true;
  
  // Vérifier si l'utilisateur est un copropriétaire (pas CS, pas gestionnaire)
  const isCoproprietaire = userProfile?.role === 'coproprietaire';
  
  // Session 100 : Vérifier si l'utilisateur est un abonné VIGIE
  const isVigieRole = userProfile?.role === 'vigie';

  // Session 188 : Vérifier si l'utilisateur est un apporteur
  // IMPORTANT : détection via apporteurInfo (double casquette vigie+apporteur possible)
  const isApporteurRole = userProfile?.role === 'apporteur';
  const hasApporteurInfo = !!userProfile?.apporteurInfo?.identifiant;
  const isApporteurEffectif = isApporteurRole || hasApporteurInfo;
  
  // Vérifier si l'utilisateur a un rôle CS (membre ou secrétaire) - vérification locale
  const isCSRole = ['cs', 'secretaire', 'membre_cs'].includes(userProfile?.role || '');
  const isCoordinateurRole = ['gestionnaire', 'coordinateur'].includes(userProfile?.role || '');

  // Session 140 : Vérification typée
  const isEnAttentePaiement = isVigieRole && userProfile ? checkEnAttentePaiement(userProfile) : false;

  // Vérification du statut utilisateur (pending/active/rejected)
  useEffect(() => {
    const checkUserStatus = async () => {
      if (!firebaseUser || !requireActive) {
        setChecking(false);
        return;
      }

      try {
        const userData = await getUserById(firebaseUser.uid);
        setUserStatus(userData?.status || 'active');
      } catch (err) {
        setUserStatus('active');
      } finally {
        setChecking(false);
      }
    };

    if (!authLoading) {
      checkUserStatus();
    }
  }, [firebaseUser, authLoading, requireActive]);

  // Session 88 : Vérification signature mandat pour les membres CS
  useEffect(() => {
    const checkMandat = async () => {
      if (
        skipMandatCheck ||
        !userProfile?.id ||
        !coproprieteId ||
        isGestionnaireSaas ||
        isCoordinateurRole ||
        isCoproprietaire ||
        isVigieRole ||
        isApporteurRole
      ) {
        setMandatCheckDone(true);
        return;
      }

      if (!isCSRole && !isCS) {
        setMandatCheckDone(true);
        return;
      }

      try {
        const mandatId = await checkSignatureRequise(coproprieteId, userProfile.id);
        if (mandatId) {
          setMandatRedirect(`/admin/mandat-signature?mandatId=${mandatId}`);
        }
      } catch (err) {
        console.error('Erreur vérification mandat:', err);
      } finally {
        setMandatCheckDone(true);
      }
    };

    if (!authLoading && !checking) {
      checkMandat();
    }
  }, [
    userProfile?.id,
    coproprieteId,
    authLoading,
    checking,
    skipMandatCheck,
    isGestionnaireSaas,
    isCoordinateurRole,
    isCoproprietaire,
    isVigieRole,
    isApporteurRole,
    isCSRole,
    isCS
  ]);

  // Session 139 : Relancer le paiement Stripe
  const handleRelancerPaiement = async () => {
    if (!userProfile?.coproprieteIds?.[0]) return;

    setIsRelancingPayment(true);
    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const createCheckoutSession = httpsCallable<
        { formule: string; coproprieteId: string },
        { sessionId: string; url: string }
      >(functions, 'createCheckoutSession');

      const result = await createCheckoutSession({
        formule: 'annuel',
        coproprieteId: userProfile.coproprieteIds[0],
      });

      if (result.data.url) {
        window.location.href = result.data.url;
      }
    } catch (err) {
      console.error('Erreur relance paiement:', err);
      setIsRelancingPayment(false);
    }
  };

  // Chargement
  if (authLoading || checking || !mandatCheckDone) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Non authentifié
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ============================================================
  // Session 139 : VIGIE en attente de paiement → écran dédié
  // ============================================================
  if (isEnAttentePaiement && !isGestionnaireSaas) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="text-amber-600" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Finalisez votre abonnement</h1>
          <p className="text-gray-600 mb-6">
            Votre compte a été créé mais le paiement n'a pas encore été finalisé. 
            Complétez votre abonnement pour accéder à votre espace VIGIE et à SENTINELLE.
          </p>
          <button
            onClick={handleRelancerPaiement}
            disabled={isRelancingPayment}
            className={`
              w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200
              ${isRelancingPayment
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-amber-600 hover:bg-amber-700 hover:shadow-lg'
              }
            `}
          >
            {isRelancingPayment ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="animate-spin" size={18} />
                Redirection vers le paiement...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <CreditCard size={18} />
                Finaliser mon abonnement
              </span>
            )}
          </button>
          <div className="mt-4 flex flex-col gap-2">
            <p className="text-xs text-gray-400">
              Paiement sécurisé par Stripe
            </p>
            <button
              onClick={() => {
                import('firebase/auth').then(({ signOut: fbSignOut }) => {
                    import('../../services/firebase').then(({ auth: fbAuth }) => {
                    fbSignOut(fbAuth).then(() => {
                      window.location.href = '/login';
                    });
                  });
                });
              }}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Se déconnecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Compte en attente (copropriétaire classique)
  if (requireActive && userStatus === 'pending') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md text-center">
          <div className="text-orange-500 text-5xl mb-4">&#x23F3;</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Compte en attente</h2>
          <p className="text-gray-600 mb-4">
            Votre inscription est en cours de validation par le conseil syndical.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Délai : 24 à 48 heures
          </p>
          <button
            onClick={() => window.location.href = '/login'}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  // Compte refusé
  if (requireActive && userStatus === 'rejected') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md text-center">
          <div className="text-red-500 text-5xl mb-4">&#x2715;</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Inscription refusée</h2>
          <p className="text-gray-600 mb-4">
            Votre demande d'inscription a été refusée.
          </p>
          <button
            onClick={() => window.location.href = '/login'}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  // Session 88 : Redirection mandat si signature requise
  if (mandatRedirect) {
    return <Navigate to={mandatRedirect} replace />;
  }

  // ============================================================
  // Session 188 : Vérification APPORTEUR
  // ============================================================
  if (requireApporteur) {
    // Admin SaaS peut toujours accéder (Dominique teste tous les parcours)
    if (isGestionnaireSaas) {
      return <>{children}</>;
    }
    // Apporteur pur (role === 'apporteur') → autorisé
    if (isApporteurRole) {
      return <>{children}</>;
    }
    // VIGIE avec apporteurInfo (double casquette — cas Alain) → autorisé
    if (hasApporteurInfo) {
      return <>{children}</>;
    }
    // Autres rôles → redirection vers leur espace
    if (isVigieRole) {
      return <Navigate to="/vigie" replace />;
    }
    if (isCoproprietaire) {
      return <Navigate to="/espace-copro" replace />;
    }
    if (isCSRole || isCoordinateurRole || isAdmin) {
      return <Navigate to="/admin" replace />;
    }
    // Fallback — écran accès réservé
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Handshake className="text-blue-600" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Espace Partenaire</h1>
          <p className="text-gray-600 mb-6">
            Cette section est réservée aux partenaires commerciaux de Copro Protect.
          </p>
          <a
            href="/login"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-block"
          >
            Retour à la connexion
          </a>
        </div>
      </div>
    );
  }

  // ============================================================
  // Session 100 : Vérification VIGIE
  // ============================================================
  if (requireVigie) {
    // Admin SaaS peut toujours accéder (Dominique teste tous les parcours)
    if (isGestionnaireSaas) {
      return <>{children}</>;
    }
    // VIGIE autorisé (même s'il est aussi apporteur — son rôle principal reste vigie)
    if (isVigieRole) {
      return <>{children}</>;
    }
    // Autres rôles → redirection vers leur espace
    if (isCoproprietaire) {
      return <Navigate to="/espace-copro" replace />;
    }
    if (isCSRole || isCoordinateurRole || isAdmin) {
      return <Navigate to="/admin" replace />;
    }
    // Fallback
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Eye className="text-amber-600" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Espace VIGIE</h1>
          <p className="text-gray-600 mb-6">
            Cette section est réservée aux abonnés de la formule VIGIE.
          </p>
          <a
            href="/login"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-block"
          >
            Retour à la connexion
          </a>
        </div>
      </div>
    );
  }

  // ============================================================
  // Session 100 / 188 : Redirection hors périmètre
  // ============================================================
  // VIGIE pur (sans apporteurInfo) → redirigé vers /vigie
  if (isVigieRole && !hasApporteurInfo && !requireVigie) {
    return <Navigate to="/vigie" replace />;
  }

  // VIGIE + apporteur (double casquette) → redirigé vers /vigie par défaut
  // (il accède à /apporteur via le sélecteur d'espace ou lien direct)
  if (isVigieRole && hasApporteurInfo && !requireVigie && !requireApporteur) {
    return <Navigate to="/vigie" replace />;
  }

  // Apporteur pur (role === 'apporteur', pas vigie) → redirigé vers /apporteur
  if (isApporteurRole && !isVigieRole && !requireApporteur) {
    return <Navigate to="/apporteur" replace />;
  }

  // Vérification Admin SaaS
  if (requireSaas && !isGestionnaireSaas) {
    if (isCoproprietaire) {
      return <Navigate to="/espace-copro" replace />;
    }
    
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="text-slate-400" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Accès réservé</h1>
          <p className="text-gray-600 mb-6">
            Cette section est réservée à l'administrateur de la plateforme Copro Protect.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/admin"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Aller à mon espace
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Vérification admin (coordinateur de copropriété)
  if (requireAdmin && !isAdmin && !isGestionnaireSaas) {
    if (isCoproprietaire) {
      return <Navigate to="/espace-copro" replace />;
    }
    
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">&#x2715;</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Accès refusé</h1>
          <p className="text-gray-600 mb-4">Cette page est réservée au coordinateur.</p>
          <a
            href="/login"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-block"
          >
            Retour à la connexion
          </a>
        </div>
      </div>
    );
  }

  // Vérification CS - REDIRECTION AUTOMATIQUE POUR COPROPRIÉTAIRES
  if (requireCS && !isCS && !isCSRole && !isAdmin && !isGestionnaireSaas) {
    if (isCoproprietaire) {
      return <Navigate to="/espace-copro" replace />;
    }
    
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">&#x2715;</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Accès refusé</h1>
          <p className="text-gray-600 mb-4">Cette page est réservée au conseil syndical.</p>
          <a
            href="/login"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-block"
          >
            Retour à la connexion
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
